import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Package, Flag, LifeBuoy, Trash2, ScrollText, BarChart3, Ban, Loader2 } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const ADMIN_TABS = [
  ["overzicht", "Overzicht"], ["users", "Gebruikers"], ["listings", "Advertenties"],
  ["reports", "Meldingen"], ["support", "Support"], ["audit", "Audit log"],
];
const TAB_ENDPOINTS = { users: "/admin/users", listings: "/admin/listings", reports: "/admin/reports", support: "/admin/support", audit: "/admin/audit-log" };
const USERS_HEAD = ["Naam", "E-mail", "Rol", "Status", ""];
const LISTINGS_HEAD = ["Titel", "Prijs", "Categorie", "Demo", ""];
const REPORTS_HEAD = ["Type", "Doel", "Reden", "Status"];
const SUPPORT_HEAD = ["Naam", "E-mail", "Bericht", "Status"];
const AUDIT_HEAD = ["Actie", "Actor", "Detail", "Tijd"];

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overzicht");
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { if (user?.role === "admin") loadStats(); }, [user]);

  const loadTab = (t) => {
    setTab(t);
    if (TAB_ENDPOINTS[t]) api.get(TAB_ENDPOINTS[t]).then((r) => setData((d) => ({ ...d, [t]: r.data })));
  };

  if (loading || user === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-[#FF6A00]" /></div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const deleteDemo = async () => {
    setBusy(true);
    try { const { data } = await api.delete("/admin/demo-listings"); toast.success(`${data.deleted_listings} demo-advertenties en ${data.deleted_users} demo-accounts verwijderd`); loadStats(); loadTab("listings"); }
    finally { setBusy(false); }
  };
  const suspend = async (id) => { await api.patch(`/admin/users/${id}/suspend`); toast.success("Bijgewerkt"); loadTab("users"); };
  const delUser = async (id) => { await api.delete(`/admin/users/${id}`); toast.success("Verwijderd"); loadTab("users"); loadStats(); };
  const delListing = async (id) => { await api.delete(`/listings/${id}`); toast.success("Verwijderd"); loadTab("listings"); loadStats(); };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-24 pt-6 md:px-8 md:pb-12" data-testid="admin-dashboard">
      <h1 className="mb-6 font-display text-3xl text-[#F5F5F5] sm:text-4xl">ADMIN DASHBOARD</h1>

      {stats && (
        <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          <SCard icon={Users} label="Gebruikers" value={stats.users} />
          <SCard icon={Package} label="Advertenties" value={stats.listings} />
          <SCard icon={Package} label="Demo" value={stats.demo_listings} />
          <SCard icon={Flag} label="Meldingen" value={stats.reports_open} />
          <SCard icon={LifeBuoy} label="Support" value={stats.support_open} />
          <SCard icon={BarChart3} label="Chats" value={stats.conversations} />
          <SCard icon={BarChart3} label="Orders" value={stats.orders} />
        </div>
      )}

      <Tabs value={tab} onValueChange={loadTab}>
        <TabsList className="mb-6 flex-wrap bg-[#1c1c1c]">
          {ADMIN_TABS.map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="data-[state=active]:bg-[#FF6A00] data-[state=active]:text-[#111]">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overzicht">
          <div className="rounded-xl border border-[#333] bg-[#1c1c1c] p-6">
            <h2 className="mb-2 font-display text-xl text-[#F5F5F5]">DEMO-CONTENT</h2>
            <p className="mb-4 text-sm text-[#A0A0A0]">Demo-advertenties zijn intern gemarkeerd en worden vervangen zodra echte gebruikers plaatsen. Verwijder ze in één klik zonder echte data te raken.</p>
            <button onClick={deleteDemo} disabled={busy} data-testid="delete-demo-btn" className="flex items-center gap-2 rounded-full bg-[#EF4444] px-5 py-2.5 font-bold text-white disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} <Trash2 className="h-4 w-4" /> Verwijder alle demo-advertenties</button>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <Table head={USERS_HEAD}>
            {(data.users || []).map((u) => (
              <tr key={u.id} className="border-t border-[#222]" data-testid={`admin-user-${u.id}`}>
                <Td>{u.name}</Td><Td>{u.email}</Td><Td>{u.role}</Td>
                <Td>{u.suspended ? <span className="text-[#EF4444]">Geschorst</span> : <span className="text-[#22C55E]">Actief</span>}</Td>
                <Td>{u.role !== "admin" && <div className="flex gap-1">
                  <IconBtn onClick={() => suspend(u.id)} title="Schorsen"><Ban className="h-4 w-4" /></IconBtn>
                  <IconBtn onClick={() => delUser(u.id)} title="Verwijderen" danger><Trash2 className="h-4 w-4" /></IconBtn>
                </div>}</Td>
              </tr>
            ))}
          </Table>
        </TabsContent>

        <TabsContent value="listings">
          <Table head={LISTINGS_HEAD}>
            {(data.listings || []).map((l) => (
              <tr key={l.id} className="border-t border-[#222]" data-testid={`admin-listing-${l.id}`}>
                <Td>{l.title}</Td><Td>{formatPrice(l.price)}</Td><Td>{l.category}</Td>
                <Td>{l.is_demo ? "Ja" : "Nee"}</Td>
                <Td><IconBtn onClick={() => delListing(l.id)} title="Verwijderen" danger data-testid={`admin-delete-listing-${l.id}`}><Trash2 className="h-4 w-4" /></IconBtn></Td>
              </tr>
            ))}
          </Table>
        </TabsContent>

        <TabsContent value="reports">
          <Table head={REPORTS_HEAD}>
            {(data.reports || []).map((r) => <tr key={r.id} className="border-t border-[#222]"><Td>{r.target_type}</Td><Td className="max-w-[160px] truncate">{r.target_id}</Td><Td>{r.reason}</Td><Td>{r.status}</Td></tr>)}
          </Table>
        </TabsContent>

        <TabsContent value="support">
          <Table head={SUPPORT_HEAD}>
            {(data.support || []).map((s) => <tr key={s.id} className="border-t border-[#222]"><Td>{s.name}</Td><Td>{s.email}</Td><Td className="max-w-[240px] truncate">{s.message}</Td><Td>{s.status}</Td></tr>)}
          </Table>
        </TabsContent>

        <TabsContent value="audit">
          <Table head={AUDIT_HEAD}>
            {(data.audit || []).map((a) => <tr key={a.id} className="border-t border-[#222]"><Td>{a.action}</Td><Td className="max-w-[140px] truncate">{a.actor_id}</Td><Td className="max-w-[200px] truncate">{a.detail}</Td><Td>{new Date(a.at).toLocaleString("nl-NL")}</Td></tr>)}
          </Table>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SCard({ icon: Icon, label, value }) { return <div className="rounded-xl border border-[#333] bg-[#1c1c1c] p-4"><Icon className="mb-1 h-5 w-5 text-[#FF6A00]" /><p className="font-display text-2xl text-[#F5F5F5]">{value}</p><p className="text-xs text-[#A0A0A0]">{label}</p></div>; }
function Table({ head, children }) { return <div className="overflow-x-auto rounded-xl border border-[#333] bg-[#1c1c1c]"><table className="w-full text-left text-sm"><thead><tr className="text-[#A0A0A0]">{head.map((h, i) => <th key={`col-${h}-${i}`} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead><tbody className="text-[#F5F5F5]">{children}</tbody></table></div>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
function IconBtn({ children, onClick, title, danger, ...rest }) { return <button onClick={onClick} title={title} {...rest} className={`rounded-full border p-2 ${danger ? "border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10" : "border-[#333] text-[#F5F5F5] hover:border-[#FF6A00]"}`}>{children}</button>; }
